import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Positions } from "@components/pages/Futures/components/Orders/Positions";
import { PositionHistory } from "@components/pages/Futures/components/Orders/PositionHistory";
import { CommunitySection } from "@components/pages/Futures/components/Orders/Community";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "@helpers/translate";
import axios from "@helpers/axios/private.axios";
import { getWallets } from "@actions/wallet";
import { showAutoCloseNotification } from "@actions/futures";
import { symbolsMatch } from "@helpers/formatOrders";
import { switchFuturesPair } from "@components/pages/Futures/components/Quotes/switchFuturesPair";
import {
  getGuideMockPositionHistory,
  getGuideMockPositions,
} from "./guideMockData";
import { OrdersLayoutProvider } from "./OrdersLayoutContext";
import { withClosedDetails } from "./closedPositions";
import {
  CONFIRM_SKIP_FLAG,
  CloseAllModal,
  useTradeConfirmSkips,
} from "../TradeModals";
import "./index.sass";
import "../../Futures.sass";

// --- CUSTOM HOOK FOR POLLING (Your Logic) ---
// Positions that leave the open list and weren't closed from here (take profit,
// stop loss, liquidation) are handed to onAutoClosed
const useFuturesPolling = ({
  endpoint,
  setData,
  prevDataRef,
  bulkCloseRef,
  manualCloseRef,
  onAutoClosed,
  dispatch,
  activeWalletId = null,
}) => {
  useEffect(() => {
    const intervalId = setInterval(() => {
      (async () => {
        try {
          const resp = await axios.get(endpoint);
          const newDataRaw = resp.data.data || [];
          const newData =
            activeWalletId == null
              ? newDataRaw
              : newDataRaw.filter((item) => item?.wallet_id === activeWalletId);

          const closedItems = prevDataRef.current.filter(
            (prevItem) =>
              !newData.find((newItem) => newItem.id === prevItem.id),
          );
          const autoClosedItems = [];

          if (closedItems.length > 0) {
            // 1. Check Global Bulk Close First
            if (bulkCloseRef.current) {
              console.log("Skipping close notifications due to bulk close.");
              bulkCloseRef.current = false;
            } else {
              // 2. Iterate through closed items individually
              closedItems.forEach((closedItem) => {
                const wasManuallyClosed = manualCloseRef?.current?.has(
                  closedItem.id,
                );

                if (wasManuallyClosed) {
                  console.log(
                    `Skipping notification for manual close: ${closedItem.id}`,
                  );
                  // Cleanup: Remove from set so memory doesn't grow indefinitely
                  manualCloseRef.current.delete(closedItem.id);
                } else {
                  // It was NOT manual (TP/SL/Liquidation)
                  autoClosedItems.push(closedItem);
                }
              });
            }
          }

          prevDataRef.current = newData;

          setData((currentData) => {
            if (newData.length !== currentData.length) {
              dispatch(getWallets());
            }

            return newData;
          });

          if (autoClosedItems.length > 0) {
            onAutoClosed(autoClosedItems);
          }
        } catch (e) {}
      })();
    }, 2000);

    return () => clearInterval(intervalId);
  }, [
    endpoint,
    dispatch,
    setData,
    prevDataRef,
    bulkCloseRef,
    manualCloseRef,
    onAutoClosed,
    activeWalletId,
  ]);
};

export const Orders = ({
  refreshTrigger = 0,
  isGuideOpen = false,
  onCloseAllPositionsChange,
}) => {
  const [activeTab1, setActiveTab1] = useState(() => {
    const saved = localStorage.getItem("futuresActiveTab1");
    if (saved === "positions" || saved === "position-history") {
      return saved;
    }
    return "positions";
  });
  const [showAllMarkets] = useState(true);

  const previousPositionsRef = useRef([]);
  const futuresPairsRef = useRef(null);

  const { user } = useSelector((state) => state.channel);
  const { pair } = useSelector((state) => state.futures);
  const activeWalletId = useSelector((state) => state.wallet.active_wallet?.id);

  const [positionsData, setPositionsData] = useState([]);
  const [positionHistoryData, setPositionHistoryData] = useState([]);
  const [positionHistoryTotal, setPositionHistoryTotal] = useState(0);

  // Зарезервовано для можливого повернення верхніх тулів
  // const [isUiAllMarketsVisible, setIsUiAllMarketsVisible] = useState(true);
  // const [isUiInstrumentVisible, setIsUiInstrumentVisible] = useState(true);
  const [isUiCancelAllVisible] = useState(true);

  const dispatch = useDispatch();
  const { isConfirmSkipped } = useTradeConfirmSkips();
  const [, setSearchParams] = useSearchParams();
  const { __ } = useTranslation();

  const isBulkClosingPositions = useRef(false);
  const [isCloseAllModalOpen, setIsCloseAllModalOpen] = useState(false);
  const isManualClosePositionRef = useRef(new Set());

  const combinedTrigger = refreshTrigger;

  const [positionHistoryRefreshKey, setPositionHistoryRefreshKey] = useState(0);

  const bumpPositionHistory = useCallback(() => {
    setPositionHistoryRefreshKey((k) => k + 1);
  }, []);

  const positionHistoryFetchKey = `${combinedTrigger}:${positionHistoryRefreshKey}`;

  const isFinitePositive = (value) =>
    Number.isFinite(Number(value)) && Number(value) > 0;

  const getOrderSymbol = useCallback((item) => {
    return item?.symbol ?? item?.pair ?? item?.pair_name ?? item?.slug ?? "";
  }, []);

  const normalizePairSlug = useCallback(
    (value) =>
      String(value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ""),
    [],
  );

  const handlePairClick = useCallback(
    async (symbol) => {
      const slug = normalizePairSlug(symbol);

      if (!slug) return;

      try {
        let pairs = futuresPairsRef.current;

        if (!pairs) {
          const response = await axios.get("/futures-pairs");

          pairs = response.data?.data || [];
          futuresPairsRef.current = pairs;
        }

        const selectedPair = pairs.find(
          (item) => normalizePairSlug(item?.slug) === slug,
        );

        if (!selectedPair) {
          console.warn(`Futures pair not found: ${slug}`);
          return;
        }

        const currentFromList = pairs.find(
          (item) => normalizePairSlug(item?.slug) === normalizePairSlug(pair?.slug),
        );

        switchFuturesPair({
          dispatch,
          setSearchParams,
          currentPair: currentFromList || pair,
          nextPair: selectedPair,
        });

        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        });
      } catch (error) {
        console.error("Failed to change futures pair:", error);
      }
    },
    [dispatch, normalizePairSlug, pair?.slug, setSearchParams],
  );

  const isRenderablePositionRow = useCallback(
    (item) => {
      if (!item?.id) return false;
      if (
        !item?.wallet_id ||
        !activeWalletId ||
        item.wallet_id !== activeWalletId
      ) {
        return false;
      }

      const status = String(item?.status || "").toLowerCase();
      const isOpenStatus = !["closed", "cancelled", "canceled"].includes(
        status,
      );

      if (!isOpenStatus) return false;

      return (
        isFinitePositive(item?.quantity) ||
        isFinitePositive(String(item?.size ?? "0").replace(/,/g, "")) ||
        isFinitePositive(item?.margin)
      );
    },
    [activeWalletId],
  );

  const isRenderablePositionHistoryRow = useCallback(
    (item) => {
      if (!item?.id) return false;
      if (
        !item?.wallet_id ||
        !activeWalletId ||
        item.wallet_id !== activeWalletId
      ) {
        return false;
      }
      if (!item?.symbol || !item?.side || !item?.created_at) return false;

      return (
        isFinitePositive(item?.quantity) ||
        isFinitePositive(String(item?.size ?? "0").replace(/,/g, "")) ||
        isFinitePositive(item?.margin)
      );
    },
    [activeWalletId],
  );

  const shouldShowGuideMockPositions =
    isGuideOpen && positionsData.length === 0;
  const shouldShowGuideMockPositionHistory =
    isGuideOpen && positionHistoryData.length === 0;

  const positionsDataForRender = useMemo(() => {
    if (!shouldShowGuideMockPositions) {
      return positionsData;
    }

    return getGuideMockPositions({
      activeWalletId,
      pair,
    });
  }, [shouldShowGuideMockPositions, positionsData, activeWalletId, pair]);

  const positionHistoryDataForRender = useMemo(() => {
    if (!shouldShowGuideMockPositionHistory) {
      return positionHistoryData;
    }

    return getGuideMockPositionHistory({
      activeWalletId,
      pair,
    });
  }, [
    shouldShowGuideMockPositionHistory,
    positionHistoryData,
    activeWalletId,
    pair,
  ]);

  const renderablePositionsData = useMemo(() => {
    return (positionsDataForRender || []).filter(isRenderablePositionRow);
  }, [positionsDataForRender, isRenderablePositionRow]);

  const renderablePositionHistoryData = useMemo(() => {
    return (positionHistoryDataForRender || []).filter(
      isRenderablePositionHistoryRow,
    );
  }, [positionHistoryDataForRender, isRenderablePositionHistoryRow]);

  const filterBySelectedMarket = useCallback(
    (items = []) => {
      if (showAllMarkets || !pair?.slug) {
        return items;
      }

      return items.filter((item) =>
        symbolsMatch(getOrderSymbol(item), pair.slug),
      );
    },
    [showAllMarkets, pair?.slug, getOrderSymbol],
  );

  const visiblePositions = useMemo(() => {
    return filterBySelectedMarket(renderablePositionsData);
  }, [filterBySelectedMarket, renderablePositionsData]);

  const visiblePositionHistory = useMemo(() => {
    return filterBySelectedMarket(renderablePositionHistoryData);
  }, [filterBySelectedMarket, renderablePositionHistoryData]);

  // Динамічний масив табів: таблиця 1 (My Position)
  const tabsTable1 = [
    {
      id: "positions",
      label: __("futures.open_trades"),
      count: visiblePositions.length,
      guideTarget: "positions-tab",
    },
    {
      id: "position-history",
      label: __("futures.closed_trades"),
      // The guide shows mock rows, the real list comes page by page
      count: shouldShowGuideMockPositionHistory
        ? visiblePositionHistory.length
        : positionHistoryTotal,
      guideTarget: "position-history-tab",
    },
  ];

  // Take profit, stop loss, liquidation or plain "Bet Closed", told apart by the
  // history record of each position
  const notifyAutoClosed = useCallback(
    async (positions) => {
      const closed = await withClosedDetails(positions);

      closed.forEach((position) => {
        dispatch(showAutoCloseNotification(position));
      });
      bumpPositionHistory();
    },
    [dispatch, bumpPositionHistory],
  );

  useFuturesPolling({
    endpoint: "/futures/positions/open",
    setData: setPositionsData,
    prevDataRef: previousPositionsRef,
    bulkCloseRef: isBulkClosingPositions,
    manualCloseRef: isManualClosePositionRef,
    onAutoClosed: notifyAutoClosed,
    dispatch,
    activeWalletId,
  });

  useEffect(() => {
    // Reset cached previous snapshots when switching wallet to avoid false
    // "position/order closed" notifications from cross-wallet diffing.
    previousPositionsRef.current = [];
    setPositionsData([]);
  }, [activeWalletId]);

  // --- BULK CLOSE HANDLERS (Parent Logic) ---
  const handleCloseAllPositions = useCallback(async () => {
    try {
      isBulkClosingPositions.current = true;
      console.log("Bulk closing positions - Notifications suppressed");

      let closePromise = Promise.resolve();
      setPositionsData((currentData) => {
        const idsToClose = currentData.map((item) => item.id);
        if (idsToClose.length > 0) {
          closePromise = axios.post(`/futures/positions/close`, {
            ids: idsToClose,
          });
        }
        return [];
      });

      dispatch(getWallets());

      try {
        await closePromise;
        bumpPositionHistory();
      } catch {
        // leave history unchanged on API failure
      }

      setTimeout(() => {
        isBulkClosingPositions.current = false;
      }, 3000);
    } catch (e) {
      console.error("Error closing all positions:", e);
      isBulkClosingPositions.current = false;
    }
  }, [dispatch, bumpPositionHistory]);

  const openCloseAllModal = useCallback(() => {
    if (isConfirmSkipped(CONFIRM_SKIP_FLAG.CLOSE_ALL)) {
      handleCloseAllPositions();
    } else {
      setIsCloseAllModalOpen(true);
    }
  }, [handleCloseAllPositions, isConfirmSkipped]);

  useEffect(() => {
    if (!onCloseAllPositionsChange) return undefined;

    onCloseAllPositionsChange({
      run: openCloseAllModal,
      disabled: visiblePositions.length === 0,
    });

    return () => {
      onCloseAllPositionsChange({
        run: null,
        disabled: true,
      });
    };
  }, [
    onCloseAllPositionsChange,
    openCloseAllModal,
    visiblePositions.length,
  ]);

  // === useEffect ДЛЯ WEBSOCKET (обробляє і ордери, і позиції) ===
  useEffect(() => {
    if (!user) return;

    const futuresEventListener = (e) => {
      const { action, tab, data } = e;
      if (!data?.id) return;
      if (
        !data?.wallet_id ||
        !activeWalletId ||
        data.wallet_id !== activeWalletId
      ) {
        return;
      }
      console.log("WebSocket Event:", { action, tab, data });

      // Оновлення відкритих ордерів
      const updateState = (setter) => {
        if (action === "add") {
          setter((prev) => [
            data,
            ...prev.filter((item) => item.id !== data.id),
          ]);
        }
        if (action === "remove") {
          setter((prev) => prev.filter((item) => item.id !== data.id));
        }
        if (action === "update") {
          setter((prev) =>
            prev.map((item) =>
              item.id === data.id ? { ...item, ...data } : item,
            ),
          );
        }
      };

      switch (tab) {
        case "positions":
          updateState(setPositionsData);
          break;
        default:
          break;
      }
    };

    user.listen("FuturesEvent", futuresEventListener);

    return () => {
      user.stopListening("FuturesEvent", futuresEventListener);
    };
  }, [user, activeWalletId]);

  const handleTabChange1 = (tabId) => {
    setActiveTab1(tabId);
    localStorage.setItem("futuresActiveTab1", tabId);
  };

  return (
    <OrdersLayoutProvider threshold={768}>
      <div className="futuresOrders">
        {/* СЕКЦІЯ 1: My Position (Open Trades / Closed Trades) */}
        <section className="futuresOrders__section futuresOrders__section--my-position">
          <h2 className="futuresOrders__title">{__("futures.my_position")}</h2>

          <div className="futuresOrders__head">
            <div
              className="futuresOrders__head-left"
              style={{ position: "relative" }}
            >
              <ul className="futuresOrders__tabs">
                {tabsTable1.map((tab) => (
                  <li key={tab.id} className="futuresOrders__tab-item">
                    <button
                      type="button"
                      className={`futuresOrders__tab ${
                        activeTab1 === tab.id ? "futuresOrders__tab--active" : ""
                      }`}
                      onClick={() => handleTabChange1(tab.id)}
                      data-futures-guide={tab.guideTarget}
                    >
                      {tab.label}
                      {tab.count > 0 && (
                        <span className="futuresOrders__tab-count">
                          ({tab.count})
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="futuresOrders__head-right">
              {isUiCancelAllVisible && (
                <button
                  onClick={openCloseAllModal}
                  type="button"
                  className="futuresOrders__close-all-btn"
                  disabled={visiblePositions.length === 0}
                >
                  {__("futures.close_all")}
                </button>
              )}
            </div>
          </div>

          <div className="futuresOrders__table-wrapper">
            <div
              className="futures__orders-positions"
              data-futures-guide={
                activeTab1 === "positions" ? "positions-table" : undefined
              }
              style={{ display: activeTab1 === "positions" ? "block" : "none" }}
            >
              <Positions
                showAllMarkets={showAllMarkets}
                data={positionsDataForRender}
                setData={setPositionsData}
                manualCloseRef={isManualClosePositionRef}
                onPositionHistoryInvalidate={bumpPositionHistory}
                onPairClick={handlePairClick}
              />
            </div>

            <div
              className="futures__orders-position-history"
              data-futures-guide={
                activeTab1 === "position-history"
                  ? "position-history-table"
                  : undefined
              }
              style={{ display: activeTab1 === "position-history" ? "block" : "none" }}
            >
              <PositionHistory
                showAllMarkets={showAllMarkets}
                data={positionHistoryDataForRender}
                setData={setPositionHistoryData}
                forceUpdate={positionHistoryFetchKey}
                isActive={activeTab1 === "position-history"}
                onTotalChange={setPositionHistoryTotal}
              />
            </div>
          </div>
          <div
            id="closed-trades-show-more-slot"
            className="closed-trades-show-more-slot"
          />
        </section>

        {/* СЕКЦІЯ 2: Community */}
        <CommunitySection />
      </div>

      <CloseAllModal
        isOpen={isCloseAllModalOpen}
        onClose={() => setIsCloseAllModalOpen(false)}
        onConfirm={async () => {
          await handleCloseAllPositions();
          setIsCloseAllModalOpen(false);
        }}
      />
    </OrdersLayoutProvider>
  );
};
